import { getRouteStackPath } from '@portal/config/routes';
import { translate } from '@portal/services/i18n';
import React from 'react';
import { myAccount, transfer, pix } from '@portal/config/app.json';
import {
  TransactionType,
  AccountTaxIdentifierType,
} from '@portal/models/enumerators';
import AccessListControl from '@portal/components/AccessListControl/AccessListControl';
import { getLimitFlowPermissions } from '@portal/utils/transaction';
import useReduxState from '@portal/hooks/useReduxState';
import AdvancedHeader from '@portal/components/AdvancedHeader/AdvancedHeader';
import AdvancedPanelContentHeader from '@portal/components/AdvancedPanelContentHeader/AdvancedPanelContentHeader';
import PixIcon from '@portal/assets/svg/ic_pix_white.svg?react';
import Barcode from '@portal/assets/svg/receive-icons/ic_barcode.svg?react';
import TedIcon from '@portal/assets/svg/ic_transfer.svg?react';
import P2PIcon from '@portal/assets/svg/ic_dollar.svg?react';
import AdvancedButton from '@portal/components/AdvancedButton/AdvancedButton';
import ThemedText from '@portal/components/ThemedText/ThemedText';
import { useNavigation } from '@portal/hooks/useNavigation';

const MyLimits = () => {
  const navigate = useNavigation();

  const {
    user: { me },
    theme: { currentTheme },
  } = useReduxState();

  const redirectTo = (stackId: string, routeId: string) => {
    navigate(getRouteStackPath(stackId, routeId));
  };

  const renderCards = () => {
    const limits = [
      {
        visible: pix.enabled,
        access: getLimitFlowPermissions(TransactionType.PIX).read,
        label: translate('PAGES.LIMITS.TITLE_PIX'),
        redirectTo: () => redirectTo('LIMITS', 'LIMITS_PIX'),
        description: translate('PAGES.LIMITS.DESCRIPTIONS.PIX'),
        icon: <PixIcon />,
      },
      {
        visible:
          pix.apiGatewayPix &&
          me.account.type === AccountTaxIdentifierType.CORPORATE,
        access: getLimitFlowPermissions(TransactionType.API_GATEWAY_PIX).read,
        label: translate('PAGES.LIMITS.TITLE_API_GATEWAY_PIX'),
        redirectTo: () => redirectTo('LIMITS', 'LIMITS_API_GATEWAY_PIX'),
        description: translate('PAGES.LIMITS.DESCRIPTIONS.API_GATEWAY_PIX'),
        icon: <PixIcon />,
      },
      {
        visible: myAccount.billetLimit,
        access: getLimitFlowPermissions(TransactionType.BILLET).read,
        label: translate('PAGES.LIMITS.TITLE_BILLET'),
        redirectTo: () => redirectTo('LIMITS', 'LIMITS_BILLET'),
        description: translate('PAGES.LIMITS.DESCRIPTIONS.BILLET'),
        icon: <Barcode />,
      },
      {
        visible: transfer.ted,
        access: getLimitFlowPermissions(TransactionType.TED).read,
        label: translate('PAGES.LIMITS.TITLE_TED'),
        redirectTo: () => redirectTo('LIMITS', 'LIMITS_TED'),
        description: translate('PAGES.LIMITS.DESCRIPTIONS.TED'),
        icon: <TedIcon />,
      },
      {
        visible: transfer.p2p,
        access: getLimitFlowPermissions(TransactionType.P2P).read,
        label: translate('PAGES.LIMITS.TITLE_P2P'),
        redirectTo: () => redirectTo('LIMITS', 'LIMITS_P2P'),
        description: translate('PAGES.LIMITS.DESCRIPTIONS.P2P'),
        icon: <P2PIcon />,
      },
    ];

    return limits
      .filter((item) => item.visible)
      .map((button) => (
        <AccessListControl accessRole={button.access} key={button.label}>
          <div className="limits-container__options__card">
            <div className="limits-container__options__card__header">
              <div
                className="limits-container__options__card__header__icon"
                style={{ background: currentTheme.background }}
              >
                {button.icon}
              </div>
              <div className="limits-container__options__card__header__info">
                <ThemedText variant="h5">{button.label}</ThemedText>
                <ThemedText variant="body">{button.description}</ThemedText>
              </div>
            </div>
            <div className="limits-container__options__card__button">
              <AdvancedButton
                size="small"
                variant="outlined"
                onClick={button.redirectTo}
                text={translate('PAGES.LIMITS.BUTTONS.DEFINE')}
              />
            </div>
          </div>
        </AccessListControl>
      ));
  };

  return (
    <>
      <AdvancedHeader
        showBreadCrumbs
        breadCrumbs={[
          {
            active: true,
            title: translate('COMPONENTS.BREADCRUMB.HOME'),
            url: getRouteStackPath('HOME', 'HOME_PAGE'),
          },
          {
            active: true,
            title: translate('PAGES.MY_ACCOUNT.TABS.ACCOUNT'),
            url: getRouteStackPath('PROFILE', 'PROFILE_PAGE'),
          },
          {
            active: false,
            title: translate('COMPONENTS.BREADCRUMB.MY_LIMITS'),
          },
        ]}
      />
      <div className="limits-container">
        <AdvancedPanelContentHeader
          testId="my-limits-header"
          title={translate('PAGES.LIMITS.LIMITS_HEADER')}
          description={translate('PAGES.LIMITS.LIMITS_SUBTITLE')}
          goBackAction={() => navigate.back()}
        />
        <div className="limits-container__options">{renderCards()}</div>
      </div>
    </>
  );
};

export default MyLimits;
